'use client';

import { c as _c } from "react/compiler-runtime";
import { ACCOUNT_SETUP_META } from '@shared/all/domains/onboarding';
import toast from '@shared/utils/helpers/toast';
import useOnboarding from '@shared/utils/hooks/useOnboarding';
import useUser from '@shared/utils/hooks/useUser';
import { Eye, EyeOff, KeyRound } from 'lucide-react';
import { useReducer } from 'react';
import { client } from '../../utils/trpc';
import Button from '../common/Button';
import Input from '../UI/Input';
import { ToggleItem } from '../UI/ToggleItem';
import OnboardingShell from './activationFunnel/OnboardingShell';

// Setup shares the funnel's split-canvas: the invited user sets a password on
// the left while brand atmosphere sits on the right, so setup and the funnel
// that follows feel like one surface.

// Account setup for magic-link accounts that have no sign-in method yet (no
// password, passkey, or linked OAuth). Feature tours are launched on demand from
// the header "?" button on each feature page.

type SetupForm = {
  username: string;
  password: string;
  confirm: string;
  showPassword: boolean;
  isPrivate: boolean;
  submitting: boolean;
};
function reducer(state: SetupForm, patch: Partial<SetupForm>): SetupForm {
  return {
    ...state,
    ...patch
  };
}
const Onboarding = () => {
  const $ = _c(69);
  const {
    currentUser
  } = useUser();
  const {
    completeAccountSetup
  } = useOnboarding(client);
  let t0;
  if ($[0] !== currentUser.username) {
    t0 = () => ({
      username: currentUser.username ?? "",
      password: "",
      confirm: "",
      showPassword: false,
      isPrivate: true,
      submitting: false
    });
    $[0] = currentUser.username;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [setup, patch] = useReducer(reducer, undefined, t0);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = /^[A-Za-z0-9_]{3,21}$/;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== setup.username) {
    t2 = t1.test(setup.username);
    $[3] = setup.username;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const usernameValid = t2;
  const passwordValid = setup.password.length >= 8;
  const passwordsMatch = setup.password === setup.confirm;
  const setupValid = usernameValid && passwordValid && passwordsMatch && !setup.submitting;
  let t3;
  if ($[5] !== completeAccountSetup || $[6] !== setup.isPrivate || $[7] !== setup.password || $[8] !== setup.username || $[9] !== setupValid) {
    t3 = async () => {
      if (!setupValid) {
        return;
      }
      patch({
        submitting: true
      });
      const error = await completeAccountSetup({
        username: setup.username,
        password: setup.password,
        isPrivate: setup.isPrivate
      });
      patch({
        submitting: false
      });
      if (error) {
        toast(error.message ?? "Failed to complete setup.", "", "error");
        return;
      }
    };
    $[5] = completeAccountSetup;
    $[6] = setup.isPrivate;
    $[7] = setup.password;
    $[8] = setup.username;
    $[9] = setupValid;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const submitSetup = t3;
  let t4;
  if ($[11] !== submitSetup) {
    t4 = () => void submitSetup();
    $[11] = submitSetup;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const t5 = !setupValid;
  let t6;
  if ($[13] !== setup.submitting || $[14] !== t4 || $[15] !== t5) {
    t6 = <Button text="Continue" onClick={t4} disabled={t5} loading={setup.submitting} classNames="w-full" />;
    $[13] = setup.submitting;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  let t8;
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="flex size-16 items-center justify-center rounded-2xl border-[1.5px]" style={{
      borderColor: "rgb(99, 102, 241)",
      backgroundColor: "rgba(99, 102, 241, 0.1)",
      color: "rgb(99, 102, 241)"
    }}><KeyRound size={30} /></div>;
    t8 = <h1 className="mt-6 font-display text-[32px] leading-[1.15] tracking-tight text-textPrimary">{ACCOUNT_SETUP_META.title}</h1>;
    t9 = <p className="mt-3 leading-relaxed text-textSecondary">{ACCOUNT_SETUP_META.description}</p>;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t7 = $[17];
    t8 = $[18];
    t9 = $[19];
  }
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = v => patch({
      username: v
    });
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== setup.username) {
    t11 = <Input type="text" label="Username" value={setup.username} onChange={t10} />;
    $[21] = setup.username;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <p className="mt-1.5 text-xs text-textSecondary/70">Letters, numbers, and underscores. 3-21 characters.</p>;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t11) {
    t13 = <div>{t11}{t12}</div>;
    $[24] = t11;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const t14 = setup.showPassword ? "text" : "password";
  let t15;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = v_0 => patch({
      password: v_0
    });
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== setup.showPassword) {
    t16 = () => patch({
      showPassword: !setup.showPassword
    });
    $[27] = setup.showPassword;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== setup.showPassword) {
    t17 = setup.showPassword ? <EyeOff size={16} /> : <Eye size={16} />;
    $[29] = setup.showPassword;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== t16 || $[32] !== t17) {
    t18 = [<button key="eye" type="button" onClick={t16} className="text-textSecondary hover:text-textPrimary" tabIndex={-1}>{t17}</button>];
    $[31] = t16;
    $[32] = t17;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== setup.password || $[35] !== t14 || $[36] !== t18) {
    t19 = <Input type={t14} label="Password" value={setup.password} onChange={t15} icons={t18} />;
    $[34] = setup.password;
    $[35] = t14;
    $[36] = t18;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <p className="mt-1.5 text-xs text-textSecondary/70">Minimum 8 characters.</p>;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== t19) {
    t21 = <div>{t19}{t20}</div>;
    $[39] = t19;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  const t22 = setup.showPassword ? "text" : "password";
  let t23;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = v_1 => patch({
      confirm: v_1
    });
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  const t24 = setup.confirm.length > 0 && !passwordsMatch;
  let t25;
  if ($[42] !== setupValid || $[43] !== submitSetup) {
    t25 = e => {
      if (e.key === "Enter" && setupValid) {
        submitSetup();
      }
      return false;
    };
    $[42] = setupValid;
    $[43] = submitSetup;
    $[44] = t25;
  } else {
    t25 = $[44];
  }
  let t26;
  if ($[45] !== setup.confirm || $[46] !== t22 || $[47] !== t24 || $[48] !== t25) {
    t26 = <Input type={t22} label="Confirm password" value={setup.confirm} onChange={t23} warning={t24} onKeyDown={t25} />;
    $[45] = setup.confirm;
    $[46] = t22;
    $[47] = t24;
    $[48] = t25;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== passwordsMatch || $[51] !== setup.confirm.length) {
    t27 = setup.confirm.length > 0 && !passwordsMatch && <p className="mt-1.5 text-xs text-destructive">Passwords don't match.</p>;
    $[50] = passwordsMatch;
    $[51] = setup.confirm.length;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  let t28;
  if ($[53] !== t26 || $[54] !== t27) {
    t28 = <div>{t26}{t27}</div>;
    $[53] = t26;
    $[54] = t27;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  let t29;
  if ($[56] !== setup.isPrivate) {
    t29 = () => patch({
      isPrivate: !setup.isPrivate
    });
    $[56] = setup.isPrivate;
    $[57] = t29;
  } else {
    t29 = $[57];
  }
  let t30;
  if ($[58] !== setup.isPrivate || $[59] !== t29) {
    t30 = <ToggleItem title="Private profile" description="When on, you only show up in spaces you participate in. Turn off to be visible across the site. You can change this anytime in Settings." state={setup.isPrivate} onChange={t29} />;
    $[58] = setup.isPrivate;
    $[59] = t29;
    $[60] = t30;
  } else {
    t30 = $[60];
  }
  let t31;
  if ($[61] !== t13 || $[62] !== t21 || $[63] !== t28 || $[64] !== t30) {
    t31 = <div className="w-full">{t7}{t8}{t9}<div className="mt-8 space-y-4">{t13}{t21}{t28}{t30}</div></div>;
    $[61] = t13;
    $[62] = t21;
    $[63] = t28;
    $[64] = t30;
    $[65] = t31;
  } else {
    t31 = $[65];
  }
  let t32;
  if ($[66] !== t31 || $[67] !== t6) {
    t32 = <OnboardingShell footer={t6}>{t31}</OnboardingShell>;
    $[66] = t31;
    $[67] = t6;
    $[68] = t32;
  } else {
    t32 = $[68];
  }
  return t32;
};
export default Onboarding;